package com.daytimegaming.ourdailyfinances.presentation.account

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.data.network.service.GeoApifyProperties
import com.daytimegaming.ourdailyfinances.data.network.service.GeoApifyService
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.realestate.RealEstateEventBus
import com.daytimegaming.ourdailyfinances.domain.usecase.RealEstateUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class AddRealEstateHoldingState(
    val nickname: String = "",
    val addressLine1: String = "",
    val addressLine2: String = "",
    val city: String = "",
    val state: String = "",
    val zipCode: String = "",
    val county: String = "",
    val purchasePrice: String = "",
    val mortgageAmount: String = "",
    val mortgageInterestRate: String = "",
    val isLoading: Boolean = false,
    val error: String? = null,
    val isSuccess: Boolean = false,
    val suggestions: List<GeoApifyProperties> = emptyList(),
    val isAutocompleteLoading: Boolean = false
) {
    val isSaveEnabled: Boolean
        get() {
            val hasRequiredTexts = nickname.isNotBlank() &&
                    addressLine1.isNotBlank() &&
                    city.isNotBlank() &&
                    state.isNotBlank() &&
                    zipCode.isNotBlank()
            val hasValidPurchasePrice = purchasePrice.filter { it.isDigit() }.toDoubleOrNull() != null
            val hasValidMortgageAmount = mortgageAmount.isEmpty() || mortgageAmount.filter { it.isDigit() }.toDoubleOrNull() != null
            val cleanInterestRate = mortgageInterestRate.filter { it.isDigit() || it == '.' }
            val hasValidMortgageInterestRate = mortgageInterestRate.isEmpty() || cleanInterestRate.toDoubleOrNull() != null
            return hasRequiredTexts && hasValidPurchasePrice && hasValidMortgageAmount && hasValidMortgageInterestRate && !isLoading
        }

    val hasAnyInput: Boolean
        get() = nickname.isNotEmpty() ||
                addressLine1.isNotEmpty() ||
                addressLine2.isNotEmpty() ||
                city.isNotEmpty() ||
                state.isNotEmpty() ||
                zipCode.isNotEmpty() ||
                county.isNotEmpty() ||
                purchasePrice.isNotEmpty() ||
                mortgageAmount.isNotEmpty() ||
                mortgageInterestRate.isNotEmpty()
}

class AddRealEstateHoldingViewModel(
    private val realEstateUseCase: RealEstateUseCase,
    private val geoApifyService: GeoApifyService,
    private val realEstateEventBus: RealEstateEventBus
) : ViewModel() {

    private val _state = MutableStateFlow(AddRealEstateHoldingState())
    val state = _state.asStateFlow()

    private var autocompleteJob: kotlinx.coroutines.Job? = null

    fun onNicknameChange(value: String) {
        _state.update { it.copy(nickname = value) }
    }

    fun onAddressLine1Change(value: String) {
        _state.update {
            it.copy(
                addressLine1 = value,
                suggestions = emptyList(),
                isAutocompleteLoading = value.length >= 3
            )
        }
        autocompleteJob?.cancel()
        if (value.length >= 3) {
            autocompleteJob = viewModelScope.launch {
                kotlinx.coroutines.delay(2000)
                try {
                    val response = geoApifyService.autocomplete(value)
                    val propsList = response.features.mapNotNull { it.properties }
                    _state.update { it.copy(suggestions = propsList, isAutocompleteLoading = false) }
                } catch (e: Exception) {
                    _state.update { it.copy(suggestions = emptyList(), isAutocompleteLoading = false) }
                }
            }
        } else {
            _state.update { it.copy(suggestions = emptyList(), isAutocompleteLoading = false) }
        }
    }

    fun onSuggestionSelect(properties: GeoApifyProperties) {
        _state.update {
            it.copy(
                addressLine1 = properties.addressLine1 ?: properties.formatted?.split(",")?.firstOrNull()?.trim() ?: it.addressLine1,
                addressLine2 = "",
                city = properties.city ?: "",
                state = properties.stateCode ?: properties.state ?: "",
                zipCode = properties.postcode ?: "",
                county = properties.county ?: "",
                suggestions = emptyList(),
                isAutocompleteLoading = false
            )
        }
    }

    fun onAddressLine2Change(value: String) {
        _state.update { it.copy(addressLine2 = value) }
    }

    fun onCityChange(value: String) {
        _state.update { it.copy(city = value) }
    }

    fun onStateChange(value: String) {
        _state.update { it.copy(state = value) }
    }

    fun onZipCodeChange(value: String) {
        _state.update { it.copy(zipCode = value) }
    }

    fun onCountyChange(value: String) {
        _state.update { it.copy(county = value) }
    }

    fun onPurchasePriceChange(value: String) {
        val digits = value.filter { it.isDigit() }
        val formatted = if (digits.isEmpty()) {
            ""
        } else {
            val parsed = digits.toLongOrNull() ?: 0L
            val formattedNumber = parsed.toString().reversed().chunked(3).joinToString(",").reversed()
            "$$formattedNumber"
        }
        _state.update { it.copy(purchasePrice = formatted) }
    }

    fun onMortgageAmountChange(value: String) {
        val digits = value.filter { it.isDigit() }
        val formatted = if (digits.isEmpty()) {
            ""
        } else {
            val parsed = digits.toLongOrNull() ?: 0L
            val formattedNumber = parsed.toString().reversed().chunked(3).joinToString(",").reversed()
            "$$formattedNumber"
        }
        _state.update { it.copy(mortgageAmount = formatted) }
    }

    fun onMortgageInterestRateChange(value: String) {
        val clean = value.filter { it.isDigit() || it == '.' }
        val formatted = if (clean.isEmpty()) {
            ""
        } else {
            val dotIndex = clean.indexOf('.')
            val base = if (dotIndex == -1) {
                clean
            } else {
                val beforeDot = clean.substring(0, dotIndex)
                var afterDot = clean.substring(dotIndex + 1).filter { it.isDigit() }
                if (afterDot.length > 2) {
                    afterDot = afterDot.substring(0, 2)
                }
                "$beforeDot.$afterDot"
            }
            "%$base"
        }
        _state.update { it.copy(mortgageInterestRate = formatted) }
    }

    fun addHolding() {
        val current = _state.value
        if (!current.isSaveEnabled) return

        _state.update { it.copy(isLoading = true, error = null) }

        viewModelScope.launch {
            try {
                val purchasePriceDouble = current.purchasePrice.filter { it.isDigit() }.toDoubleOrNull() ?: 0.0
                val mortgageAmountDouble = current.mortgageAmount.filter { it.isDigit() }.toDoubleOrNull()
                val mortgageInterestRateDouble = current.mortgageInterestRate
                    .filter { it.isDigit() || it == '.' }
                    .toDoubleOrNull()

                val response = realEstateUseCase.CreateRealEstateHolding(
                    nickname = current.nickname.trim(),
                    addressLine1 = current.addressLine1.trim(),
                    addressLine2 = current.addressLine2.trim().ifEmpty { null },
                    city = current.city.trim(),
                    state = current.state.trim(),
                    zipCode = current.zipCode.trim(),
                    county = current.county.trim().ifEmpty { null },
                    purchasePrice = purchasePriceDouble,
                    mortgageAmount = mortgageAmountDouble,
                    mortgageInterestRate = mortgageInterestRateDouble
                )

                when (response) {
                    is Response.Success -> {
                        realEstateEventBus.emitEvent()
                        _state.update { it.copy(isLoading = false, isSuccess = true) }
                    }
                    is Response.Error -> {
                        _state.update { it.copy(isLoading = false, error = response.message) }
                    }
                    is Response.Loading -> {
                        // Handled by isLoading = true
                    }
                }
            } catch (e: Exception) {
                _state.update { it.copy(isLoading = false, error = e.message ?: "An unexpected error occurred") }
            }
        }
    }
}
